import { useState, useCallback } from "react";
import { FILE_SIZE_LIMITS } from "../constants";

interface FileUploadState {
  file: File | null;
  isDragOver: boolean;
  error: string | null;
}

interface UseFileUploadOptions {
  maxFileSize?: number;
  allowedExtensions?: string[];
  onFileSelect?: (file: File) => void;
}

interface UseFileUploadReturn extends FileUploadState {
  handleFileSelect: (file: File) => void;
  handleFileRemove: () => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  clearError: () => void;
}

export function useFileUpload(
  options: UseFileUploadOptions = {}
): UseFileUploadReturn {
  const {
    maxFileSize = FILE_SIZE_LIMITS.MAX_FILE_SIZE,
    allowedExtensions,
    onFileSelect
  } = options;

  const [state, setState] = useState<FileUploadState>({
    file: null,
    isDragOver: false,
    error: null
  });

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxFileSize) {
        const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
        return `File size must be less than ${maxSizeMB}MB`;
      }

      if (allowedExtensions && allowedExtensions.length > 0) {
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!extension || !allowedExtensions.includes(extension)) {
          return `File type not allowed. Allowed types: ${allowedExtensions.join(
            ", "
          )}`;
        }
      }

      return null;
    },
    [maxFileSize, allowedExtensions]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      const error = validateFile(file);

      if (error) {
        setState((prev) => ({ ...prev, error, file: null }));
        return;
      }

      setState((prev) => ({ ...prev, file, error: null }));
      onFileSelect?.(file);
    },
    [validateFile, onFileSelect]
  );

  const handleFileRemove = useCallback(() => {
    setState((prev) => ({ ...prev, file: null, error: null }));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState((prev) => ({ ...prev, isDragOver: true }));
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState((prev) => ({ ...prev, isDragOver: false }));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setState((prev) => ({ ...prev, isDragOver: false }));

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    handleFileSelect,
    handleFileRemove,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    clearError
  };
}
